import React from "react";
import { Modal, Button, Card, Row, Col, Form as BootstrapForm } from "react-bootstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import LocationSearchDropdown from "../components/LocationAdd2"; // Adjust import path if needed

// Simplified Validation Schema for Flatmates
const flatmateValidationSchema = Yup.object().shape({
  address: Yup.string().required("Address is required"),
  pinCode: Yup.string().required("Pin code is required"),
  minBudget: Yup.number()
    .typeError("Must be a number")
    .required("Minimum budget is required")
    .positive("Must be greater than zero"),
  maxBudget: Yup.number()
    .typeError("Must be a number")
    .required("Maximum budget is required")
    .positive("Must be greater than zero")
    .moreThan(Yup.ref("minBudget"), "Max budget must be greater than min budget"),
  moveInTimeline: Yup.string().required("Timeline is required"),
});

const FlatmateEditModal = ({ show, onHide, flatData, onSave, isSaving }) => {
  if (!flatData) return null;

  // Initializing only the specific fields required
  const initialValues = {
    id: flatData._id || "",
    address: flatData.address || "",
    pinCode: flatData.pinCode || "",
    minBudget: flatData.minBudget || "",
    maxBudget: flatData.maxBudget || "",
    moveInTimeline: flatData.moveInTimeline || "",
    // Retaining hidden baseline metadata to avoid overwriting structural backend values
    street: flatData.street || "",
    city: flatData.city || "",
    state: flatData.state || "",
    country: flatData.country || "",
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ color: "#E5484D", fontWeight: "bold" }}>
          Edit Flatmate Requirements
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: "#FFE4E6" }}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={flatmateValidationSchema}
          onSubmit={(values) => {
            onSave(values);
          }}
        >
          {({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              
              {/* LOCATION SECTION */}
              <Card className="border-0 shadow-sm p-4 mb-4 rounded-4">
                <h5 className="mb-3 fw-bold text-dark">Target Location</h5>
                <div className="mb-3">
                  <LocationSearchDropdown
                    onSelect={(locationData) => {
                      setFieldValue("address", locationData.name || "");
                      setFieldValue("pinCode", locationData.pincode || "");
                      setFieldValue("street", locationData.streetAddress || "");
                      setFieldValue("city", locationData.city || "");
                      setFieldValue("state", locationData.state || "");
                      setFieldValue("country", locationData.country || "");
                    }}
                  />
                </div>

                <Row className="g-3">
                  <Col md={8}>
                    <BootstrapForm.Group>
                      <BootstrapForm.Label className="fw-semibold">Preferred Area / Address</BootstrapForm.Label>
                      <BootstrapForm.Control
                        name="address"
                        value={values.address}
                        onChange={handleChange}
                        isInvalid={touched.address && !!errors.address}
                        placeholder="e.g. Sector 17, Chandigarh"
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.address}
                      </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>
                  </Col>

                  <Col md={4}>
                    <BootstrapForm.Group>
                      <BootstrapForm.Label className="fw-semibold">Pin Code</BootstrapForm.Label>
                      <BootstrapForm.Control
                        name="pinCode"
                        value={values.pinCode}
                        onChange={handleChange}
                        isInvalid={touched.pinCode && !!errors.pinCode}
                        placeholder=""
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.pinCode}
                      </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>
                  </Col>
                </Row>
              </Card>

              {/* BUDGET & TIMELINE SECTION */}
              <Card className="border-0 shadow-sm p-4 mb-4 rounded-4">
                <h5 className="mb-3 fw-bold text-dark">Budget & Timeline</h5>
                <Row className="g-3">
                  <Col md={4}>
                    <BootstrapForm.Group>
                      <BootstrapForm.Label className="fw-semibold">Min Budget (₹)</BootstrapForm.Label>
                      <BootstrapForm.Control
                        type="number"
                        name="minBudget"
                        value={values.minBudget}
                        onChange={handleChange}
                        isInvalid={touched.minBudget && !!errors.minBudget}
                        placeholder="Min Budget"
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.minBudget}
                      </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>
                  </Col>

                  <Col md={4}>
                    <BootstrapForm.Group>
                      <BootstrapForm.Label className="fw-semibold">Max Budget (₹)</BootstrapForm.Label>
                      <BootstrapForm.Control
                        type="number"
                        name="maxBudget"
                        value={values.maxBudget}
                        onChange={handleChange}
                        isInvalid={touched.maxBudget && !!errors.maxBudget}
                        placeholder="Max Budget"
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.maxBudget}
                      </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>
                  </Col>

                  <Col md={4}>
                    <BootstrapForm.Group>
                      <BootstrapForm.Label className="fw-semibold">Move-In Timeline</BootstrapForm.Label>
                      <BootstrapForm.Select
                        name="moveInTimeline"
                        value={values.moveInTimeline}
                        onChange={handleChange}
                        isInvalid={touched.moveInTimeline && !!errors.moveInTimeline}
                      >
                        <option value="">Select...</option>
                        <option value="immediate">Immediately</option>
                        <option value="within2Weeks">Within 2 Weeks</option>
                        <option value="within1Month">Within 1 Month</option>
                        <option value="flexible">Flexible</option>
                      </BootstrapForm.Select>
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.moveInTimeline}
                      </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>
                  </Col>
                </Row>
              </Card>

              {/* ACTION BUTTONS */}
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button variant="outline-secondary" onClick={onHide} disabled={isSaving} className="px-4 rounded-pill">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSaving} 
                  style={{ backgroundColor: "#E5484D", borderColor: "#E5484D" }}
                  className="px-4 rounded-pill"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>

            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default FlatmateEditModal;